import { useEffect } from "react";
import { toast } from "sonner";
import { X, Flag } from "lucide-react";
import { calculateAge, formatDOB, formatHeight } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ProfileModal = ({ profile, onClose, onInterested }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const reportProfile = () => {
    const reason = window.prompt(
      "Please describe your concern about this profile (minimum 10 characters):",
    );
    if (!reason) return;
    api
      .post("/reports", { type: "profile", profile_id: profile.id, reason })
      .then(() => toast.success("Thank you. Your report has been submitted to Suffa Matrimony for review."))
      .catch((e) => toast.error(formatApiError(e)));
  };

  if (!profile) return null;

  const age = profile.age ?? calculateAge(profile.dob);
  const showFullDOB = profile.privacy.dob === "full_dob" && profile.dob;

  return (
    <div className="fm-modal-overlay" data-testid="profile-modal" onClick={onClose}>
      <div
        className="fm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Profile ${profile.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fm-modal-close"
          data-testid="fm-modal-close"
          aria-label="Close profile"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        <div className="fm-modal-head">
          <span className="fm-medallion fm-medallion-lg" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 40 40" fill="none">
              <g stroke="#C5A059" strokeWidth="1.5" fill="none">
                <rect x="12.5" y="12.5" width="15" height="15" />
                <rect x="12.5" y="12.5" width="15" height="15" transform="rotate(45 20 20)" />
              </g>
              <circle cx="20" cy="20" r="2.6" fill="#C5A059" />
            </svg>
          </span>
          <div>
            <span className="fm-profile-id">{profile.id}</span>
            <h3 className="fm-modal-title">
              {profile.gender === "bride" ? "Bride" : "Groom"} · {profile.city}, {profile.countryShort}
            </h3>
          </div>
        </div>

        <dl className="fm-modal-facts">
          <div>
            <dt>{t("Age / Date of Birth")}</dt>
            <dd data-testid="fm-modal-age">
              {showFullDOB
                ? `${t("Age")} ${age} · ${t("Born")} ${formatDOB(profile.dob)} ${t("(shared with permission)")}`
                : `${t("Age")}: ${age}`}
            </dd>
          </div>
          <div>
            <dt>{t("Marital Status")}</dt>
            <dd>{profile.maritalStatus}</dd>
          </div>
          <div>
            <dt>Education</dt>
            <dd>{profile.education}</dd>
          </div>
          <div>
            <dt>Profession</dt>
            <dd>{profile.profession}</dd>
          </div>
          {profile.heightCm && (
            <div>
              <dt>{t("Height")}</dt>
              <dd>{formatHeight(profile.heightCm)}</dd>
            </div>
          )}
        </dl>

        {profile.intro && <p className="fm-modal-intro">{profile.intro}</p>}

        <p className="fm-modal-privacy">
          {t(
            "Contact details are never shown publicly. Connections are made only through Suffa Matrimony, with family and dignity, insha’Allah.",
          )}
        </p>

        <div className="fm-modal-actions">
          <button
            type="button"
            className="btn btn-solid btn-lg"
            data-testid="fm-modal-interested"
            onClick={() => onInterested(profile)}
          >
            {t("Interested")}
          </button>
          <button type="button" className="btn btn-outline btn-lg" onClick={onClose}>
            {t("Close")}
          </button>
        </div>

        {user && (
          <button
            type="button"
            className="fm-more-toggle"
            data-testid="fm-modal-report"
            style={{ margin: "10px auto 0", display: "flex" }}
            onClick={reportProfile}
          >
            <Flag size={13} aria-hidden="true" /> Report this profile
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
